import React, { useState } from "react";
import { Box, MenuItem, Select, Button, CircularProgress, FormControl } from "@mui/material";
// ATS helpers
import { GetIndustryTypeHelper } from "../../ATS/helper/Ancillary/IndustryTypeHelper";
import { GetSkillsHelper } from "../../ATS/helper/Ancillary/SkillsHelper";
import { GetJobTemplateHelper } from "../../ATS/helper/Ancillary/JobTemplateHelper";
import { GetEmploymentTypeHelper } from "../../Ancillary/EmploymentType/helper/EmploymentTypeHelper";
import { GetFormTemplateHelper, GetOfferLetterTemplateHelper } from "../../ATSSettings/helper/AtsFormTemplateHelper";
import { GetCtcCategoryHelper } from "../../ATS/helper/Ancillary/CtcCategoryHelper";
import { GetDocumentationsHelper } from "../../../Components/Ancillary/Documentations/helper/documentationsHelper";
// General Setting helpers
import { GetDesignationHelper } from "../../Ancillary/Designation/helper/DesignationHelper";
import { GetLeavingReasonHelper } from "../../Ancillary/LeavingReason/helper/LeavingReasonHelper";
import { GetBusinessUnitHelper } from "../../Ancillary/BusinessUnit/helper/BusinessUnitHelper";
import { GetSubBusinessUnitHelper } from "../../Ancillary/SubBusinessUnit/helper/SubBusinessUnitHelper";
import { GetCostCenterHelper } from "../../Ancillary/CostCenter/helper/CostCenterHelper";
import { GetProofTypeHelper } from "../../Ancillary/ProofType/helper/ProofTypeHelper";
import { GetStudyLevelHelper } from "../../Ancillary/StudyLevel/helper/StudyLevelHelper";
import { GetStudyProgramHelper } from "../../Ancillary/StudyProgram/helper/StudyProgramHelper";
import { GetLeaveTypesHelper } from "../../Leave/helper/LeaveTypeHelper";
import { GetYearlyHolidayCalendarHelper } from "../../Leave/helper/YearlyHolidayCalendarHelper";
import { GetLeaveReasonHelper } from "../../Leave/helper/LeaveReasonHelper";
import { GetShiftTimeConfigureHelper } from "../../Leave/helper/ShiftTimeConfigureHelper";
import { GetWorkLocationHelper } from "../../Ancillary/WorkLocation/helper/WorkLocation";
import { GetWeekOffConfigureHelper, GetWorkHoursHelper } from "../../Leave/helper/WeekOffHelper";
import { GetReportScheduleHelper } from "../../Ancillary/ReportScheduler/helper/ReportSchedulerHelper";
import { GetECMSExitReasonHelper } from "../../Ancillary/ECMS/helper/ExitReasonHelper";
import { GetECMSClearanceTypeHelper } from "../../Ancillary/ECMS/helper/ClearanceHelper";
import { GetAITokenConfigHelper } from "../../Ancillary/AIConfig/helper/ApiKeyHelper";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

// ─── ATS columns mapping ──────────────────────────────────────────────────────
const atsColumnsMapping = {
  "Industry Sector": [{ title: "Industry Sector", field: "industry_name" }, { title: "Meepl Configuration", field: "is_public_data" }],
  "Skills": [{ title: "Industry Sector", field: "industry_name" }, { title: "Skill", field: "skill" }, { title: "Meepl Configuration", field: "is_public_data" }],
  "JD Template": [{ title: "Industry Sector", field: "industry_name" }, { title: "Job Title", field: "job_title" }, { title: "Meepl Configuration", field: "is_public_data" }],
  "CTC Configuration": [{ title: "Category", field: "category_label" }, { title: "Min Range", field: "min_range" }, { title: "Max Range", field: "max_range" }],
  "Employment Type": [{ title: "Name", field: "etype_name" }, { title: "Meepl Configuration", default: "is_default" }],
  "Documentations": [{ title: "Proof Type (Display Name)", field: "proof_name" }, { title: "Cloud Space Name", field: "proof_folder_name" }],
  "Form Builder": [{ title: "Template Name", field: "template_name" }],
  "Offer Letter": [{ title: "Template Name", field: "offer_letter_template_name" }],
};

// ─── General columns mapping ──────────────────────────────────────────────────
const generalColumnsMapping = {
  "Cost Center": [{ title: "Name", field: "name" }, { title: "Business Unit", field: "businessunit" }, { title: "Sub Business Unit", field: "subbusinessunit" }],
  "Designation": [{ title: "Designation", field: "name" }],
  "Leaving Reason": [{ title: "Leaving Reason", field: "lreason_name" }],
  "Business Unit": [{ title: "Name", field: "name" }, { title: "BU Alias", field: "alias_name" }],
  "Sub Business Unit": [{ title: "Name", field: "name" }, { title: "Business Unit", field: "businessunit" }],
  "Employment Type": [{ title: "Name", field: "etype_name" }, { title: "Meepl Configuration", default: "is_default" }],
  "Proof Type": [{ title: "Name", field: "name" }, { title: "Proof Length", field: "identity_length" }, { title: "Document Required", field: "document_required" }, { title: "Meepl Configuration", default: "is_public_data" }], // if "is_public_data" === 1
  "Study Level": [{ title: "Name", field: "name" }, { title: "Code", field: "code" }],
  "Study Program": [{ title: "Study Level", field: "studylevel" }, { title: "Program Name", field: "name" }],
  "Leave Types": [{ title: "Name", field: "name" }, { title: "Code", field: "code" }, { title: "Credit Type", field: "leave_type" }, { title: "Credit Days", render: (item) => item.leave_type === "flat" ? item.actual_days : item?.credit_type === "monthly_accrual" ? item?.monthly_credit : item?.credit_type === "yearly_accrual" ? item?.yearly_credit : "-" }, { title: "Carry Forward?", field: "cfwd_leave" }, { title: "Carry Forward Days", field: "noof_cfwd_days" }, { title: "Gender Based", field: "gender_based" }],
  "Holidays": [{ title: "Holiday Name", field: "name" }, { title: "Date", field: "date" }],
  "Leave Reason": [{ title: "Leave Reason", field: "reason_name" }],
  "Shift Time": [{ title: "Shift Name", field: "shift_name" }, { title: "Time In", field: "in_time" }, { title: "Time Out", field: "out_time" }, { title: "Pay", field: "pay" }],
  "Work Location": [{ title: "Work Location Name", field: "work_location_name" }, { title: "State", field: "state_name" }, { title: "Region", field: "region_name" }, { title: "Meepl Configuration", field: "is_default" }],
  "Week Off Configuration": [{ title: "Day", field: "day_name" }, { title: "Full Day", field: "full_day_off" }, { title: "Half Day", field: "half_day_off" }],
  "Work Hours": [{ title: "Work Hour Code", field: "work_hour_code" }, { title: "Work Hour", field: "working_hours" }, { title: "Meepl Configuration", field: "is_default" }],
  "Report Scheduler": [{ title: "Report", field: "reports_list_name" }, { title: "Email", field: "email" }, { title: "Schedule Type", field: "occurence" }],
  "Asset Configuration": [{ title: "Asset Type", field: "clearance_type" }, { title: "Approver", field: "employee_name" }],
  "Exit Reason Config": [{ title: "Reason", field: "exit_reason" }, { title: "Meepl Configuration", field: "is_default" }],
  "AI Configuration": [{ title: "AI Provider", field: "ai_type" }, { title: "Model", render: (item) => item?.ai_token_json?.display_name }, { title: "Resume Processor", field: "resume_processor_selection" }, { title: "Profile Scoring", field: "profile_scoring_selection" }, { title: "ATS Job", field: "jd_generation_selection" }],
};

// ─── Helper: extract data from response ──────────────────────────────────────
const extractData = (res) => res?.data?.statuscode === 200 ? res?.data?.data : [];

// ─── Build section arrays ─────────────────────────────────────────────────────
const buildATSSections = (d) => [
  { title: "Industry Sector", data: d?.industryTypeData || [] },
  { title: "Skills", data: d?.skillsData || [] },
  { title: "JD Template", data: d?.jobTemplateData || [] },
  { title: "CTC Configuration", data: d?.ctcCategory || [] },
  { title: "Employment Type", data: d?.employment_type_options || [] },
  { title: "Documentations", data: d?.documentations_options || [] },
  { title: "Form Builder", data: d?.templateData || [] },
  { title: "Offer Letter", data: d?.offerletterData || [] },
];

const buildGeneralSections = (d) => [
  { title: "Cost Center", data: d?.cost_center_options || [] },
  { title: "Designation", data: d?.designation_options || [] },
  { title: "Leaving Reason", data: d?.leavingreasonData || [] },
  { title: "Business Unit", data: d?.businessunitData || [] },
  { title: "Sub Business Unit", data: d?.subBusinessunitData || [] },
  { title: "Employment Type", data: d?.employment_type_options || [] },
  { title: "Proof Type", data: d?.proof_type_data || [] },
  { title: "Study Level", data: d?.studylevel_options || [] },
  { title: "Study Program", data: d?.studyprogram_options || [] },
  { title: "Leave Types", data: d?.leavetypes_options || [] },
  { title: "Holidays", data: d?.holidayData || [] },
  { title: "Leave Reason", data: d?.leavereasonData || [] },
  { title: "Shift Time", data: d?.shift_time_options || [] },
  { title: "Work Location", data: d?.workLocation_options || [] },
  { title: "Week Off Configuration", data: d?.week_off_data || [] },
  { title: "Work Hours", data: d?.work_hours_data || [] },
  { title: "Report Scheduler", data: d?.report_schedule_data || [] },
  { title: "Asset Configuration", data: d?.clearanceTypeData || [] },
  { title: "Exit Reason Config", data: d?.exitReasonsData || [] },
  { title: "AI Configuration", data: d?.aiConfigData || [] },
];

// ─── Shared export utilities ──────────────────────────────────────────────────
const getCellValue = (item, col) => {
  let val;
  // Conditional field
  if (col?.render) {
    val = col.render(item);
  } else if (col?.default) { // Handle default (Yes/No case)
    const valRaw = item[col.default];
    val = ["1", 1, true].includes(valRaw) ? "Yes" : "No";
  } else { // Normal field
    val = item[col?.field];
  }
  if (typeof val === "boolean") val = val ? "Yes" : "No";
  else if (typeof val === "object" && val !== null) val = JSON.stringify(val);
  return val === undefined || val === null ? "" : val;
};

const exportToExcel = (sections, columnsMap, fileName) => {
  const wb = XLSX.utils.book_new();
  sections?.forEach((section) => {
    const sheetData = section?.data?.length > 0 ? section.data : [{}];
    const mapping = columnsMap[section.title];
    const cleanData = sheetData?.map((item) => {
      if (mapping) {
        const mappedItem = {};
        mapping.forEach((col) => { mappedItem[col.title] = getCellValue(item, col); });
        return mappedItem;
      } else {
        const { tableData, ...rest } = item;
        const out = {};
        for (const key in rest) {
          out[key] = typeof rest[key] === "object" && rest[key] !== null ? JSON.stringify(rest[key]) : rest[key];
        }
        return out;
      }
    });
    const ws = XLSX.utils.json_to_sheet(cleanData);
    if (ws["!ref"]) {
      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[addr]) ws[addr].s = { fill: { fgColor: { rgb: "fecb03" } }, font: { bold: true, color: { rgb: "000000" } }, alignment: { horizontal: "left", vertical: "center" } };
      }
    }
    XLSX.utils.book_append_sheet(wb, ws, section.title.substring(0, 31).replace(/[\\\/?*[\]]/g, ""));
  });
  XLSX.writeFile(wb, fileName);
};

const exportToPdf = (sections, columnsMap, fileName) => {
  const doc = new jsPDF("l", "mm", "a4");
  let isFirstPage = true;
  sections?.forEach((section) => {
    const sheetData = section?.data?.length > 0 ? section.data : [];
    if (sheetData.length === 0) return;
    const mapping = columnsMap[section.title];
    let headCols = [];
    let bodyRows = [];
    if (mapping) {
      headCols = mapping.map((col) => col.title);
      bodyRows = sheetData.map((item) => mapping.map((col) => getCellValue(item, col)));
    } else {
      const { tableData, ...rest } = sheetData[0];
      headCols = Object.keys(rest);
      bodyRows = sheetData.map((item) => {
        const { tableData, ...restItem } = item;
        return headCols.map((key) => {
          let v = restItem[key];
          if (typeof v === "object" && v !== null) v = JSON.stringify(v);
          return v === undefined || v === null ? "" : v?.toString();
        });
      });
    }
    if (!isFirstPage) doc.addPage(); else isFirstPage = false;
    doc.setFontSize(16);
    doc.text(`${section.title}`, 14, 15);
    autoTable(doc, { head: [headCols], body: bodyRows, startY: 20, theme: "grid", headStyles: { fillColor: [254, 203, 3], textColor: [0, 0, 0], fontStyle: "bold" } });
  });
  doc.save(fileName);
};

// ─── Component ────────────────────────────────────────────────────────────────
const SettingConfigExportPopup = ({ exportPopupData }) => {
  const [settingType, setSettingType] = useState("General");
  const [loading, setLoading] = useState(null);

  const fetchSettingsDataExport = async (exportType) => {
    const domain_name = exportPopupData?.domainname;
    const companyName = exportPopupData?.company_full_name || "Export";
    setLoading(exportType);
    try {
      if (settingType === "ATS") {
        const [industryTypeData, skillsData, jobTemplateData, employment_type_options, documentations_options, templateData, ctcCategory, offerletterData] = await Promise.all([
          GetIndustryTypeHelper({ domain_name }),
          GetSkillsHelper({ domain_name }),
          GetJobTemplateHelper({ domain_name }),
          GetEmploymentTypeHelper({ domain_name }),
          GetDocumentationsHelper({ domain_name }),
          GetFormTemplateHelper({ workspace: domain_name }),
          GetCtcCategoryHelper({ domain_name }),
          GetOfferLetterTemplateHelper({ workspace: domain_name }),
        ]);
        const preparedData = {
          industryTypeData: extractData(industryTypeData),
          skillsData: extractData(skillsData),
          jobTemplateData: extractData(jobTemplateData),
          ctcCategory: extractData(ctcCategory),
          employment_type_options: extractData(employment_type_options),
          documentations_options: extractData(documentations_options),
          templateData: extractData(templateData),
          offerletterData: extractData(offerletterData),
        };
        const sections = buildATSSections(preparedData);
        if (exportType === "pdf") exportToPdf(sections, atsColumnsMapping, `${companyName}_ATS_Settings.pdf`);
        else exportToExcel(sections, atsColumnsMapping, `${companyName}_ATS_Settings.xlsx`);

      } else {
        // General Settings — fetch all configs with domain_name param
        const [
          designationRes, leavingReasonRes, businessUnitRes, subBusinessUnitRes,
          employmentTypeRes, costCenterRes, proofTypeRes, studyLevelRes, studyProgramRes,
          leaveTypesRes, holidayRes, leaveReasonRes, shiftTimeRes, workLocationRes,
          weekOffRes, workHoursRes, reportScheduleRes, exitReasonRes, clearanceTypeRes, aiConfigRes,
        ] = await Promise.all([
          GetDesignationHelper({ domain_name }),
          GetLeavingReasonHelper({ domain_name }),
          GetBusinessUnitHelper({ domain_name }),
          GetSubBusinessUnitHelper({ domain_name }),
          GetEmploymentTypeHelper({ domain_name }),
          GetCostCenterHelper({ domain_name }),
          GetProofTypeHelper({ domain_name }),
          GetStudyLevelHelper(),
          GetStudyProgramHelper({ studylevel_id: 0 }),
          GetLeaveTypesHelper({ domain_name }),
          GetYearlyHolidayCalendarHelper({ domain_name, year: 0 }),
          GetLeaveReasonHelper({ domain_name }),
          GetShiftTimeConfigureHelper({ domain_name }),
          GetWorkLocationHelper({ domain_name }),
          GetWeekOffConfigureHelper({ domain_name }),
          GetWorkHoursHelper({ domain_name }),
          GetReportScheduleHelper({ domain_name }),
          GetECMSExitReasonHelper({ domain_name }),
          GetECMSClearanceTypeHelper({ domain_name }),
          GetAITokenConfigHelper({ domain_name }),
        ]);

        const preparedData = {
          designation_options: extractData(designationRes),
          leavingreasonData: extractData(leavingReasonRes),
          businessunitData: extractData(businessUnitRes),
          subBusinessunitData: extractData(subBusinessUnitRes),
          employment_type_options: extractData(employmentTypeRes),
          cost_center_options: extractData(costCenterRes),
          proof_type_data: extractData(proofTypeRes),
          studylevel_options: extractData(studyLevelRes),
          studyprogram_options: extractData(studyProgramRes),
          leavetypes_options: extractData(leaveTypesRes),
          holidayData: extractData(holidayRes),
          leavereasonData: extractData(leaveReasonRes),
          shift_time_options: extractData(shiftTimeRes),
          workLocation_options: extractData(workLocationRes),
          week_off_data: extractData(weekOffRes),
          work_hours_data: extractData(workHoursRes),
          report_schedule_data: reportScheduleRes?.data?.statuscode === 200 ? reportScheduleRes?.data?.data?.[0]?.fn_get_report_schedule : [],
          exitReasonsData: extractData(exitReasonRes),
          clearanceTypeData: extractData(clearanceTypeRes),
          aiConfigData: extractData(aiConfigRes),
        };

        const sections = buildGeneralSections(preparedData);
        if (exportType === "pdf") exportToPdf(sections, generalColumnsMapping, `${companyName}_General_Settings.pdf`);
        else exportToExcel(sections, generalColumnsMapping, `${companyName}_General_Settings.xlsx`);
      }
    } catch (err) {
      console.error("Export fetch error:", err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Box p={1} display="flex" flexDirection="column" gap={2} alignItems="flex-start">
      <FormControl fullWidth size="small">
        <Select size="small" value={settingType} onChange={(e) => setSettingType(e.target.value)}>
          <MenuItem value="General">General Settings</MenuItem>
          <MenuItem value="ATS">ATS Settings</MenuItem>
        </Select>
      </FormControl>
      <Box display="flex" gap={2} alignItems="center">
        <Button variant="outlined" onClick={() => fetchSettingsDataExport("excel")} disabled={!!loading}>
          {loading === "excel" ? <CircularProgress size={24} sx={{ mr: 1, color: "inherit" }} /> : <FileDownloadIcon sx={{ mr: 1, color: "inherit" }} />}
          Export as Excel
        </Button>
        <Button variant="outlined" onClick={() => fetchSettingsDataExport("pdf")} disabled={!!loading}>
          {loading === "pdf" ? <CircularProgress size={24} sx={{ mr: 1, color: "inherit" }} /> : <PictureAsPdfIcon sx={{ mr: 1, color: "inherit" }} />}
          Export as PDF
        </Button>
      </Box>
    </Box>
  );
};

export default SettingConfigExportPopup;